/****************************************************************************************************
 * Imports
 ****************************************************************************************************/
import { useCallback, useEffect, useState, useMemo } from "react";
import {
  ApiException,
  StocktakeClient,
  StocktakeDTO,
} from "../../../services/ApiClient";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ICellRendererParams,
  NewValueParams,
  ValueFormatterParams,
} from "ag-grid-community";
import useWindowDimensions from "../../../ultilities/UseWIndowDimensions";
import { NoCards } from "../../layouts/NoCards";
import { Card, Col } from "react-bootstrap";
import { CardPlaceholder } from "../../common/cardPlaceholder/CardPlaceholder";
import { DateRenderer } from "../../common/dateRenderer/DateRenderer";
import { DeleteItemRenderer } from "../../common/deleteItemRenderer/DeleteItemRenderer";

/****************************************************************************************************
 * Interfaces
 ****************************************************************************************************/
interface IReviewStocktakeProps {
  pageTitle: string;
}
interface IModalDetails {
  title: string;
  message: string;
}

/****************************************************************************************************
 * Component
 ****************************************************************************************************/
export const ReviewStocktakes = (props: IReviewStocktakeProps) => {
  //console.log("Rendering ReviewStocktakes...");
  const { width } = useWindowDimensions();

  /************************************************************************************************
   * States
   ************************************************************************************************/
  const [isFetching, setIsFetching] = useState(true);
  const [modalDetails, setModalDetails] = useState<IModalDetails>({
    title: "",
    message: "",
  });

  //const [Stocktakes, setStocktakes] = useImmer<StocktakeDTO[] | null>(null);
  const [Stocktakes, setStocktakes] = useState<StocktakeDTO[] | null>(null);

  // Add to states section
  const [searchFilter, setSearchFilter] = useState<string>("");

  //console.log("Stocktakes in Component", Stocktakes);

  /************************************************************************************************
   * Effects
   ************************************************************************************************/
  useEffect(() => {
    //console.log("Executing useEffect...");
    const getStocktakes = async () => {
      try {
        const Stocktakes = await new StocktakeClient(
          process.env.REACT_APP_API_BASE
        ).getStocktakesForReview(90);
        setStocktakes(Stocktakes);
        //console.log("Stocktakes in useEffect", Stocktakes);
      } catch (e: unknown) {
        const error = e as ApiException;
        console.log("getStocktakes error: " + error.message);
      } finally {
        setIsFetching(false);
      }
    };

    getStocktakes().catch(console.error);
  }, [setStocktakes]);

  /************************************************************************************************
   * Support Functions
   ************************************************************************************************/
  const deleteStocktake = useCallback(
    async (Stocktake: StocktakeDTO) => {
      try {
        if (Stocktakes) {
          await new StocktakeClient(
            process.env.REACT_APP_API_BASE
          ).deleteStocktake(Stocktake.stocktakeId);
          //console.log("Deleting Stocktake " + JSON.stringify(Stocktake.StocktakeId));
          const StocktakesCopy = Stocktakes.filter(
            (item) => item.stocktakeId !== Stocktake.stocktakeId
          );
          setStocktakes(StocktakesCopy);

          // setStocktakes((draft) => {
          //     return draft?.filter((item) => item.StocktakeId !== Stocktake.StocktakeId);
          // });
        }
      } catch (e: unknown) {
        const error = e as ApiException;
        setModalDetails({
          title: "Error",
          message: JSON.parse(error.response).errorMessages[0],
        });
      }
    },
    [Stocktakes]
  );

  const onStocktakePriceChanged = useCallback(
    async (p: NewValueParams) => {
      //console.log("onStocktakePriceChanged", p.data);
      try {
        if (Stocktakes) {
          await new StocktakeClient(
            process.env.REACT_APP_API_BASE
          ).updateStocktake(p.data);
          const StocktakesCopy = [...Stocktakes];
          const itemToUpdate = StocktakesCopy.find(
            (item) => item.stocktakeId === p.data.StocktakeId
          );
          if (itemToUpdate) itemToUpdate.originalPrice = Number(p.newValue);
          setStocktakes(StocktakesCopy);
        }
      } catch (e: unknown) {
        const error = e as ApiException;
        setModalDetails({
          title: "Error",
          message: JSON.parse(error.response).errorMessages[0],
        });
      }
    },
    [Stocktakes]
  );

  const onQtyChanged = useCallback(
    async (p: NewValueParams) => {
      //console.log("onQtyChanged", p.data);
      try {
        if (Stocktakes) {
          await new StocktakeClient(
            process.env.REACT_APP_API_BASE
          ).updateStocktake(p.data);
          const StocktakesCopy = [...Stocktakes];
          const itemToUpdate = StocktakesCopy.find(
            (item) => item.stocktakeId === p.data.StocktakeId
          );
          if (itemToUpdate) itemToUpdate.quantity = Number(p.newValue);
          setStocktakes(StocktakesCopy);
        }
      } catch (e: unknown) {
        const error = e as ApiException;
        setModalDetails({
          title: "Error",
          message: JSON.parse(error.response).errorMessages[0],
        });
      }
    },
    [Stocktakes]
  );

  // Add this function in the support functions section
  const filteredStocktakes = useMemo(() => {
    if (!Stocktakes || !searchFilter) return Stocktakes;

    return Stocktakes.filter((item) =>
      item.itemName.toLowerCase().includes(searchFilter.toLowerCase())
    );
  }, [Stocktakes, searchFilter]);

  /************************************************************************************************
   * Table Cell Formatters
   ************************************************************************************************/
  const currencyFormatter = (p: ValueFormatterParams) => "$" + p.value;

  /************************************************************************************************
   * Table Column Definitions
   ************************************************************************************************/
  const defaultColDef: ColDef = {
    flex: 1,
    sortable: true,
    lockPosition: true,
    resizable: true,
    filter: "agTextColumnFilter",
    floatingFilter: true,
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Date",
        field: "stocktakeDate",
        cellDataType: "date",
        cellRenderer: DateRenderer,
        width: 120,
        filter: "agDateColumnFilter",
        valueGetter: (params) => {
          return params.data?.stocktakeDate ? new Date(params.data.stocktakeDate) : null;
        },
      },
      {
        headerName: "Store",
        field: "storeName",
        width: 100,
      },
      {
        headerName: "Item",
        field: "itemName",
        minWidth: 100,
      },
      {
        headerName: "Comment",
        field: "comment",
        flex: 2,
      },
      {
        headerName: "Original Price",
        cellHeaderStyle: { "text-align": "center" },
        field: "originalPrice",
        editable: true,
        valueFormatter: currencyFormatter,
        onCellValueChanged: onStocktakePriceChanged,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Qty",
        field: "quantity",
        editable: true,
        type: "number",
        maxWidth: 90,
        minWidth: 90,
        onCellValueChanged: onQtyChanged,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "",
        field: "StocktakeId",
        flex: 1,
        filter: false,
        cellRendererSelector: (params: ICellRendererParams<any>) => {
          const button = {
            component: DeleteItemRenderer,
          };
          return button;
        },
        cellRendererParams: {
          deleteItemCallback: deleteStocktake,
        },
      },
    ],
    [deleteStocktake, onStocktakePriceChanged, onQtyChanged]
  );

  //console.log("Stocktakes before render", Stocktakes);

  /************************************************************************************************
   * Render
   ************************************************************************************************/
  return (
    <>
      <NoCards pageTitle={"Review Stocktake"}>
        {!isFetching && Stocktakes && Stocktakes.length > 0 ? (
          <>
            <Col lg={12}>
              <Card className="shadow-sm" border="light">
                {/*<div className="search-container">
                  <Form.Control
                    type="text"
                    placeholder="Search by product name..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                  />
                </div>
                  */}
                <div className="ag-theme-alpine ag-row-pointer available-products-container">
                  <AgGridReact
                    rowData={filteredStocktakes}
                    //ref={props.gridRef}
                    className={`ag-theme-alpine`}
                    columnDefs={columnDefs}
                    domLayout="autoHeight"
                    singleClickEdit={true}
                    alwaysShowHorizontalScroll={false}
                    defaultColDef={defaultColDef}
                    pagination={true}
                    paginationPageSize={50}
                  />
                </div>
              </Card>
            </Col>
          </>
        ) : null}

        {!isFetching && (Stocktakes === null || Stocktakes?.length === 0) ? (
          <Col lg={12}>
            <Card className="shadow-sm" border="light">
              <div className="no-items">No items available.</div>
            </Card>
          </Col>
        ) : null}

        {isFetching ? (
          <Col lg={12}>
            <CardPlaceholder />
          </Col>
        ) : null}
      </NoCards>
    </>
  );
};
